<template>
  <modal :open="internalOpen" :width="900">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('pages.additionalPayments.bulkStatus.title') }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <Multiselect
        v-model="rawStatus"
        :error="errors?.status"
        :options="statusOptions"
        :label="$t('pages.additionalPayments.status')"
        class="w-full mb-5"
        :can-deselect="false"
        :can-clear="false"
      />
      <div class="w-full text-center">
        <custom-button
          variant="primary"
          class="mt-15"
          :busy="loading"
          @click="save"
        >
          {{ $t('save') }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import Multiselect from '@utils/Multiselect.vue';
import CustomButton from '@utils/CustomButton.vue';
import { enumOptionsProvider } from '@utils/table/hooks/provider';
import { useFetchAction } from '@hooks/fetch';
import { useNotify } from '@utils/notifications';

interface Props {
  open: boolean;
  payoutIds: string[];
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const { t } = useI18n();
const notify = useNotify();

const rawStatus = ref<string | undefined>(undefined);
const {
  data: status,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  string().nullable().required(t('validation.notEmpty')),
  rawStatus,
);

const statusOptions = enumOptionsProvider('/enums/AdditionalPayoutStatusEnum', {}, (item) => ({
  label: t(`pages.additionalPayments.statusValues.${item}`),
  value: String(item),
}));

function resetData() {
  setValidation(false);
  rawStatus.value = undefined;
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    await exec('/payout_additional/bulk-change-status', {
      method: 'POST',
      body: {
        payoutIds: props.payoutIds,
        status: status.value,
      },
    });
    notify?.({
      variant: 'success',
      title: t('pages.additionalPayments.bulkStatus.saveSuccess'),
    });
    emit('saved');
    closeModal();
  } catch {
    notify?.({
      variant: 'error',
      title: t('pages.additionalPayments.bulkStatus.saveError'),
    });
  }
});
</script>
